import { handleApiError } from '@api';
import config from '@config';
import { HTTPClient, isAbortError } from '@platform2-lib/httpz';
import * as schema from '@schema';
import type { Audience, Dataset, RelevantAudience } from 'types/schema';

const client = new HTTPClient(
  config.env,
  `${config.api.origin}/spark-agent-v2/audiences`
);

async function getAudienceDetails(
  audienceIds: Audience['id'][]
): Promise<Audience[]> {
  try {
    const response = await client.post(
      `/details`,
      {
        ['audience_ids']: audienceIds
      },
      schema.AudienceDetailsResponse
    );

    return response;
  } catch (error: unknown) {
    handleApiError(error, 'Failed to retrieve the audience details.', true);
  }
}

export interface AudienceSearchProps {
  searchTerm: string;
  currentIds?: ReadonlyArray<RelevantAudience['id']>;
  datasetCode?: Dataset['id'];
  abortSignal?: AbortSignal;
}

async function search({
  searchTerm,
  currentIds,
  datasetCode,
  abortSignal
}: AudienceSearchProps): Promise<RelevantAudience[]> {
  const queryParams = new URLSearchParams();
  queryParams.set('searchTerm', searchTerm);

  if (currentIds && currentIds.length > 0) {
    queryParams.set('audienceIds', currentIds.join(','));
  }

  if (datasetCode) {
    queryParams.set('datasetCode', datasetCode);
  }

  try {
    const response = await client.get(
      `/relevant?${queryParams.toString()}`,
      schema.RelevantAudiencesResponse,
      {
        signal: abortSignal
      }
    );

    return response.audiences;
  } catch (error: unknown) {
    if (isAbortError(error)) {
      return [];
    }

    handleApiError(error, 'Failed to retrieve the relevant audiences.');

    return [];
  }
}

export const audiences = {
  getAudienceDetails,
  search
};
